import {has, omit} from "lodash";
import * as pMap from "p-map";
import * as merge from 'deepmerge';
import {v4 as uuidv4} from "uuid";
import {
  AnnotationTypes,
  Block,
  BlockType,
  BoundTransformerDef,
  Color,
  Entry,
  EntryDTO,
  HTMLTags,
  Node,
  NodeMap,
  NodeType,
  Property,
  SerializerAttributes,
} from "../../contract/notion";
import ContentApi from "../../wrapper";
import {ContentApiDef} from "../../index";

type FindNodeDef = (
  self: ContentApiDef,
  model: string,
  key: string
) => Record<string, unknown>;
export const findNode: FindNodeDef = (
  self,
  model,
  key
) => {
  const {routes} = self;

  let node;

  // prefer exact match, if not fall back to parent
  if (has(routes, key)) {
    node = routes[key];
  } else if (routes) {
    const inexact = Object.keys(routes).find(node => key.startsWith(node)); // @todo allow this to be configured

    node = routes[inexact];
  }

  return node;
}

// flatten EntryDTOs to HTML string
type FlattenDef = (items: EntryDTO[]) => string;

export const flatten: FlattenDef = (
  items = []
) => {
  const result = '';

  return !items.length
    ? result
    : items.reduce((result, item) => {
      const {html} = item;

      return `${result}${html}`;
    }, result);
};

// render Blocks to EntryDTOs
type TransformBlocksDef = (
  self: ContentApi,
  transformer: BoundTransformerDef,
  data: Block[],
  block: Block,
  index: number
) => Promise<EntryDTO>

export const transformBlocks: TransformBlocksDef = async (
  self,
  transformer,
  data = [],
  block,
  index
) => {
  const recursive = self.options.get('transformer.recursive');
  const previous = index > 0 ? data[index - 1] : null;
  const next = index < data.length ? data[index + 1] : null;

  const {object, type, has_children, id, ...rest} = block;
  if (type === BlockType.UNSUPPORTED || object !== 'block') return;

  const blockType = type.toUpperCase() as keyof typeof BlockType;
  const verifiedBlockType: string = BlockType[blockType] as const;
  const nodes: NodeMap = has(block, type)
    ? block[type as BlockType]
    : null;
  const content = nodes[NodeType.TEXT as NodeType]; // we can only work with text nodes, for now
  const nodeAttributes = omit(nodes, NodeType.TEXT as NodeType);

  let children;
  if (!has_children) {
    children = {results: []};
  } else if (has_children && recursive) {
    children = {results: nodes.children};
  } else {
    children = await self.queue.add(() => self._getNode(id), {id});
  }

  let child_nodes: Node[] = [];
  if (has(children, 'results') && Array.isArray(children.results) && children.results.length) {
    child_nodes = await pMap(children.results, transformBlocks.bind(null, self, transformer, children.results), {concurrency: 3})
  }

  const {items, attributes} = transformer(
    {
      previous,
      next,
      type: type as BlockType,
      node: {
        ...nodeAttributes, // checked, title, url
      },
      block: {
        id: block.id,
        type: verifiedBlockType as BlockType,
        object: 'block',
        ...rest
      },
    },
    content,
    child_nodes.filter(n => n)
  );

  return items.join('').length ? {
    html: items.join(''),
    attributes: {...attributes, id: block.id}
  } : null;
}

// render Blocks to EntryDTOs
type TransformBlocksSyncDef = (
  self: ContentApi,
  transformer: BoundTransformerDef,
  data: Block[],
  block: Block,
  index: number
) => EntryDTO

export const transformBlocksSync: TransformBlocksSyncDef = (
  self,
  transformer,
  data = [],
  block,
  index
) => {
  const previous = index > 0 ? data[index - 1] : null;
  const next = index < data.length ? data[index + 1] : null;

  const {...rest} = block;
  const type = block?.type ?? BlockType.TEXT;
  const object = block?.object ?? 'block';

  if (type === BlockType.UNSUPPORTED || object !== 'block') return;

  const blockType = type.toUpperCase() as keyof typeof BlockType;
  const verifiedBlockType: string = BlockType[blockType] as const;
  const nodes: NodeMap|null = has(block, type)
    ? block[type as BlockType]
    : null;

  if (!nodes) return;

  const content = nodes[NodeType.TEXT]; // we can only work with text nodes, for now
  const nodeAttributes = omit(nodes, NodeType.TEXT);

  const {items, attributes} = transformer(
    {
      previous,
      next,
      type: type as BlockType,
      node: {
        ...nodeAttributes, // checked, title, url
      },
      block: {
        id: block.id,
        type: verifiedBlockType as BlockType,
        object: 'block',
        ...rest
      },
    },
    content,
    []
  );

  return items.join('').length ? {
    html: items.join(''),
    attributes: {...attributes, id: block.id}
  } : null;
}

// resolve attributes
type GetNodeAttributesDef = (
  obj: Node,
  attributes: SerializerAttributes,
) => SerializerAttributes & {
  annotations: {
    bold: boolean;
    code: boolean;
    italic: boolean;
    strikethrough: boolean;
    underline: boolean;
  };
  content: string;
  link: any;
  source: any;
  style: string;
  tag: string;
  checked: boolean;
};

export const getNodeAttributes: GetNodeAttributesDef = (
  obj,
  attributes,
) => {
  let type = obj.type;

  // if node is an anchor, imperatively set the type
  const node = obj.text;
  if (obj.href) type = NodeType.ANCHOR;
  const tag: HTMLTags = HTMLTags[type.toUpperCase() as keyof typeof HTMLTags];

  const {
    block,
    node: {checked},
    hasMultipleNodes,
  } = attributes;

  const {
    color,
    ...annotations
  } = obj.annotations;

  // replace color with custom color if passed
  const colorKey = color.split('_')[0].toUpperCase() as keyof typeof Color;
  const customColor: Color = has(Color, colorKey)
    ? Color[colorKey]
    : Color.DEFAULT;

  // set content
  const {source, link} = node;
  if (link?.url) type = NodeType.ANCHOR;

  // only set style if passed
  const property =
    customColor && color.includes('background')
      ? `style="background-color:${customColor.split('_')[0]}"`
      : `style="color:${customColor}"`;

  // use ternary operator to return empty string instead of undefined
  const style = color && color !== 'default' ? ` ${property}` : '';

  // set type to break if no content exists
  if ((!node.content || node.content === "\n") && type !== NodeType.DIVIDER) {
    node.content = ''; // opinionated: strip new-line to style element as line-break
    type = NodeType.BREAK;
  }

  return {
    annotations,
    block,
    content: node.content,
    checked,
    hasMultipleNodes,
    link,
    source,
    type,
    tag,
    style,
  }
}

// render a Node with annotations
type GetAnnotatedContentDef = (
  annotations: any,
  children: EntryDTO[],
  content: string,
) => string;

export const getAnnotatedContent: GetAnnotatedContentDef = (
  annotations,
  children,
  content,
) => {
  const hasAnnotations = Object.entries((annotations || {}))
    .filter(([value, annotation]) => value && annotation)
    .map(([annotation, value]) => annotation); // eslint-disable-line @typescript-eslint/no-unused-vars

  let i = -1;
  let annotatedContent = content;

  while (++i < hasAnnotations.length) {
    const tag = AnnotationTypes[hasAnnotations[i].toUpperCase() as keyof typeof AnnotationTypes];

    annotatedContent = `<${tag}>${annotatedContent}</${tag}>`;
  }

  return `${annotatedContent}${flatten(children)}`;
}

type GetContentDef = (
  block: Block,
  attributes: {
    checked: boolean;
    hasMultipleNodes: boolean;
    link: any;
    source: any;
    style: string;
    tag: string;
    type: string;
  },
  annotate: (content: string) => string,
  content: string
) => string;

export const getContent: GetContentDef = (
  block,
  {
    checked,
    hasMultipleNodes,
    link,
    source,
    style,
    tag,
    type,
  },
  annotate,
  content
) => {
  switch (type) {
    case 'heading_1':
    case 'heading_2':
    case 'heading_3': {
      return `<${tag}${style}>${annotate(content)}</${tag}>`;
    }
    case 'divider':
    case 'break': {
      return `<${tag} />`;
    }
    case 'numbered_list_item':
    case 'bulleted_list_item': {
      return `<${tag}${style}>${annotate(content)}</${tag}>`;
    }
    case 'toggle':
    case 'toggle_item': {
      return `<${tag} aria-expanded="false"${style}>${annotate(content)}</${tag}>`;
    }
    case 'to_do':
    case 'to_do_item': {
      return `<${tag} for="${block.id}"${style}>${annotate(content)}</${tag}><input id="${block.id}" name="${block.id}" value="${block.id}" type="checkbox"${checked ? ' checked' : ''} />`;
    }
    case 'image': {
      return `<${tag}${style} src="${source}" />`;
    }
    case 'anchor': {
      return `<${tag} href="${link.url}"${style}>${annotate(content)}</${tag}>`;
    }
    default: {
      if (hasMultipleNodes) {
        return annotate(content);
      } else if (tag) {
        return `<${tag}${style}>${annotate(content)}</${tag}>`;
      } else {
        return null;
      }
    }
  }
}

type PropertyToBlockDef = (
  entry: Entry,
  property: Property,
  type: BlockType,
  position?: number,
) => Entry;

export const propertyToBlock: PropertyToBlockDef = (
  entry,
  property,
  type,
  position = -1,
) => {
  // return if the property is invalid or empty
  if (
    !property ||
    !property[property.type] ||
    !Array.isArray(property[property.type]) ||
    !property[property.type].length
  ) return entry;

  const {nodes: blocks} = entry;

  const block = {
    ...property,
    object: 'block',
    id: uuidv4(),
    has_children: false,
    type,
  }

  block[type] = {
    text: property[property.type as BlockType]
  };

  if (position < 1) {
    blocks[!position ? 'unshift' : 'push'](block); // add block to start (0) or end (truthy)
  } else { // add block to specific position
    const semantic_blocks = getSemanticBlocks(blocks || []);

    let pos = -1;
    pos = Array.isArray(semantic_blocks) && position <= semantic_blocks.length
      // zero-indexed
      ? (position - 1 < 0) ? 0 : (position - 1)
      : (semantic_blocks.length - 1 < 0) ? 0 : (semantic_blocks.length - 1);

    if (pos > -1 && (pos < semantic_blocks.length) && semantic_blocks[pos]) {
      const {id} = semantic_blocks[pos];
      const prev_block = blocks.find(block => block.id === id);
      const prev_block_position = blocks.indexOf(prev_block) + 1;
      blocks.splice(prev_block_position, 0, block);
    }
  }

  entry.nodes = blocks;
  return entry;
}

const SKIP_BLOCKS = [
  BlockType.HEADING_1,
  BlockType.HEADING_2,
  BlockType.HEADING_3
];

const SKIP_GROUPED_BLOCKS = [
  BlockType.BULLETED_LIST_ITEM,
  BlockType.NUMBERED_LIST_ITEM,
  BlockType.TOGGLE,
  BlockType.TO_DO,
];

type GetSemanticBlocksDef = (
  blocks: Block[]
) => Block[];

export const getSemanticBlocks: GetSemanticBlocksDef = (
  blocks
) => {
  return (Array.isArray(blocks) ? blocks : []).reduce((result, block, index) => {
    const next = blocks[index + 1];

    if (next?.type) {
      if (SKIP_BLOCKS.indexOf(block.type as BlockType) > -1) {
        if (block.type !== next.type) return result;
      }

      if (SKIP_GROUPED_BLOCKS.indexOf(block.type as BlockType) > -1) {
        if (block.type === next.type) return result;
      }
    }

    result.push(block);
    return result;
  }, []);
}

type CombinePropertiesDef = (
  entry: Entry,
  target: string,
  properties: Property[]
) => Entry;

export const combineProperties: CombinePropertiesDef = (
  entry,
  target,
  properties,
) => {
  entry.properties[target] = (properties || []).reduce((result, property) => {
    result = merge(result, property)

    return result;
  }, {});

  return entry;
}

type NodeToBlockDef = (
  block: Block,
  nodes: Node[],
  type: BlockType,
) => Block;

export const nodesToBlock: NodeToBlockDef = (
  block,
  nodes = [],
  type,
) => {
  const {type: previous, ...rest} = block; // eslint-disable-line @typescript-eslint/no-unused-vars

  // clean-up
  delete rest[previous];

  return {
    ...rest,
    id: uuidv4(),
    type,
    [type]: {
      text: nodes
    }
  }
}
